import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, X } from 'lucide-react';
import { Card, CardHeader, CardBody, CardFooter } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import api from '@/lib/api';
import { JobCategory, JobType, Chain, Currency } from '@hirenfire/shared';
import type { ApiResponse, Job } from '@hirenfire/shared';

export function CreateJobPage() {
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<JobType>(JobType.TASK);
  const [category, setCategory] = useState<JobCategory>(JobCategory.VA);
  const [budget, setBudget] = useState('');
  const [currency, setCurrency] = useState<Currency>(Currency.USDC);
  const [chain, setChain] = useState<Chain>(Chain.POLYGON);
  const [timezone, setTimezone] = useState('');
  const [shiftsPerWeek, setShiftsPerWeek] = useState('');
  const [hoursPerShift, setHoursPerShift] = useState('');
  const [requirements, setRequirements] = useState<string[]>([]);
  const [newRequirement, setNewRequirement] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const addRequirement = () => {
    if (newRequirement.trim()) {
      setRequirements([...requirements, newRequirement.trim()]);
      setNewRequirement('');
    }
  };

  const removeRequirement = (index: number) => {
    setRequirements(requirements.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const payload = {
        title,
        description,
        type,
        category,
        budget: Number(budget),
        currency,
        chain,
        requirements,
        timezone: timezone || null,
        shiftsPerWeek: shiftsPerWeek ? Number(shiftsPerWeek) : null,
        hoursPerShift: hoursPerShift ? Number(hoursPerShift) : null,
      };

      const res = await api.post<ApiResponse<Job>>('/jobs', payload);
      if (res.data.data) {
        navigate(`/jobs/${res.data.data.id}`);
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data
          ?.error?.message ?? 'Failed to create job. Please try again.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>

      <div>
        <h1 className="text-2xl font-bold text-white">Post a New Job</h1>
        <p className="text-gray-400 mt-1">Fill in the details to create your job listing</p>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>Job Details</CardHeader>
          <CardBody className="space-y-5">
            {error && (
              <div className="p-3 bg-error-500/10 border border-error-500/30 rounded-lg text-error-400 text-sm">
                {error}
              </div>
            )}

            <Input
              label="Job Title"
              placeholder="e.g., OnlyFans Chatter - Evening Shifts"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />

            <Textarea
              label="Description"
              placeholder="Describe the role, responsibilities, and what you're looking for..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxChars={5000}
              required
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Job Type"
                value={type}
                onChange={(e) => setType(e.target.value as JobType)}
                options={Object.values(JobType).map((t) => ({ value: t, label: t }))}
              />
              <Select
                label="Category"
                value={category}
                onChange={(e) => setCategory(e.target.value as JobCategory)}
                options={Object.values(JobCategory).map((c) => ({ value: c, label: c }))}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Budget"
                type="number"
                placeholder="1000"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                required
              />
              <Select
                label="Currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value as Currency)}
                options={Object.values(Currency).map((c) => ({ value: c, label: c }))}
              />
              <Select
                label="Blockchain"
                value={chain}
                onChange={(e) => setChain(e.target.value as Chain)}
                options={Object.values(Chain).map((c) => ({ value: c, label: c }))}
              />
            </div>

            <Input
              label="Timezone (optional)"
              placeholder="e.g., EST, PST, UTC+2"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
            />

            {type === JobType.SHIFT && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Shifts Per Week"
                  type="number"
                  placeholder="5"
                  value={shiftsPerWeek}
                  onChange={(e) => setShiftsPerWeek(e.target.value)}
                />
                <Input
                  label="Hours Per Shift"
                  type="number"
                  placeholder="8"
                  value={hoursPerShift}
                  onChange={(e) => setHoursPerShift(e.target.value)}
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">
                Requirements
              </label>
              <div className="flex gap-2 mb-3">
                <Input
                  placeholder="Add a requirement..."
                  value={newRequirement}
                  onChange={(e) => setNewRequirement(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addRequirement();
                    }
                  }}
                />
                <Button type="button" variant="secondary" onClick={addRequirement}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {requirements.length > 0 && (
                <div className="space-y-2">
                  {requirements.map((req, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-2 bg-dark-700/50 rounded-lg"
                    >
                      <span className="text-sm text-gray-300">{req}</span>
                      <button
                        type="button"
                        onClick={() => removeRequirement(i)}
                        className="text-gray-500 hover:text-error-400 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardBody>
          <CardFooter>
            <div className="flex justify-end gap-3">
              <Button variant="secondary" type="button" onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button type="submit" isLoading={isLoading}>
                Create Job
              </Button>
            </div>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
}
